'use client';

import { TypingIndicator } from '@/components/common/TypingIndicator';
import InspirationDialogue from '@/components/inspiration-dialogue';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { signOut, useSession } from 'next-auth/react';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';

export default function LoggedInHomeNew() {
  const { data: session, status } = useSession();

  const [revealedCount, setRevealedCount] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  const progress = session?.user?.progress ? session?.user?.progress : null;
  const lastActivePath =
    progress !== 'complete' && progress !== null && progress !== 'null'
      ? session?.user?.progress
      : session?.user?.lastActivePath || '/chat';

  const role = session?.user?.role;

  const [showActions, setShowActions] = useState(false);

  const [steps, setSteps] = useState(['greetNew']);

  const messagesEndRef = useRef(null);

  // store timeout ids so we can clear them when component unmounts or step changes
  const timersRef = useRef([]);

  // You can tweak these to control the pacing / animation timing
  const TYPING_DURATION = 800; // ms: how long typing indicator shows before revealing a message
  const BETWEEN_MESSAGES_DELAY = 300;

  useEffect(() => {
    // whenever steps change, reveal messages for the last step with delays and typing animation
    // clear any pending timers first
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];

    const lastStep = steps[steps.length - 1];
    const stepData = flow[lastStep];

    if (!stepData || !Array.isArray(stepData.messages)) {
      setRevealedCount(0);
      setIsTyping(false);
      return;
    }

    // start fresh
    setRevealedCount(0);
    setIsTyping(true);

    const messagesCount = stepData.messages.length;

    // schedule events: for each message, first show typing for TYPING_DURATION, then reveal it
    let scheduleBase = 0;
    for (let i = 0; i < messagesCount; i++) {
      // start typing for message i
      const startTypingAt = scheduleBase;
      const typingTimer = setTimeout(() => {
        setIsTyping(true);
      }, startTypingAt);
      timersRef.current.push(typingTimer);

      // reveal message after typing duration
      const revealAt = startTypingAt + TYPING_DURATION;
      const revealTimer = setTimeout(() => {
        setRevealedCount((prev) => prev + 1);
        setIsTyping(false);
      }, revealAt);
      timersRef.current.push(revealTimer);

      // prepare next base: after reveal, wait a bit before starting typing for next message
      scheduleBase = revealAt + BETWEEN_MESSAGES_DELAY;
    }

    // After all messages revealed, ensure typing is false and optionally show actions
    const finishTimer = setTimeout(() => {
      setIsTyping(false);
      setShowActions(true);
    }, scheduleBase);
    timersRef.current.push(finishTimer);

    //

    // cleanup on unmount or next run
    return () => {
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current = [];
    };
  }, [steps]);

  // Add this effect to handle auto-scrolling
  useEffect(() => {
    if (steps <= 2) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [revealedCount, steps]);

  const flow = {
    greetNew: {
      messages: [
        <>
          Welcome back <b className="capitalize">{session?.user?.firstName}</b>. It’s good to see
          you again.
        </>,
        <>
          <p>
            Since you’ve already explored SmartGuy®, you can jump right back into anything you
            need…
          </p>
        </>,
        <>
          <p>What would you like to do today?</p>
        </>,
      ],
      options: [
        { label: 'Videos', url: '/chat' },
        { label: 'Blogs', url: '/blogs' },
        { label: 'Assessments', url: '/assessment' },
        {
          label: 'Continue where I left off',
          url: role === 'consumer' ? `${lastActivePath}` : '/signin',
        },
        {
          label: 'Daily Inspiration',
          ...(role === 'consumer'
            ? {
                button: (
                  <div className="flex flex-wrap justify-start gap-3">
                    <InspirationDialogue />
                  </div>
                ),
              }
            : { url: '/signin' }),
        },

        // {
        //   label: 'Personal',
        //   next: 'personal',
        // },
      ],
    },
    personal: {
      messages: [<>Would you like to focus on</>],
      options: [
        { label: 'Videos', url: '/chat' },
        { label: 'Blogs', url: '/blogs' },
        { label: 'Assessments', url: '/assessment' },
        {
          label: 'Continue where I left off',
          url: role === 'consumer' ? `${lastActivePath}` : '/signin',
        },
        {
          label: 'Daily Inspiration',
          ...(role === 'consumer'
            ? {
                button: (
                  <div className="flex flex-wrap justify-start gap-3">
                    <InspirationDialogue />
                  </div>
                ),
              }
            : { url: '/signin' }),
        },
      ],
    },
  };

  return (
    <main className="mt-5">
      <div className="absolute top-0 right-0 px-3 py-2 sm:px-5 sm:py-3 lg:px-10 lg:py-6">
        <div className="flex justify-between gap-x-3">
          <Button
            variant={'outline'}
            className={cn('border-dark-blue text-dark-blue w-full rounded-full')}
            onClick={() => signOut()}
          >
            Logout
          </Button>
        </div>
      </div>

      <ScrollArea className="min-h-screen overflow-y-auto bg-white">
        <main className="mx-auto flex w-full max-w-3xl flex-col flex-wrap justify-start gap-y-4">
          {steps.map((step, index) => {
            const isActiveStep = index === steps.length - 1;
            const messages = flow[step].messages ?? [];

            const msgCount = messages.length;

            return (
              <React.Fragment key={index}>
                {messages.map((message, msgIndex) => {
                  // if this step is active, we compare msgIndex with revealedCount; otherwise reveal all previous steps' messages
                  const visible = !isActiveStep || msgIndex < revealedCount;

                  return (
                    <React.Fragment key={msgIndex}>
                      {visible ? (
                        <Message key={step + '-' + msgIndex} content={message} visible={visible} />
                      ) : (
                        ''
                      )}
                    </React.Fragment>
                  );
                })}

                {isActiveStep && isTyping && (
                  <div className="mb-2">
                    <TypingIndicator />
                  </div>
                )}

                {steps.length > 2 && <div ref={messagesEndRef} />}

                {(!isActiveStep || revealedCount >= msgCount) && (
                  <div className="flex flex-wrap justify-end gap-3">
                    {flow[step].options.map((option, index) => (
                      <div className="flex flex-wrap justify-start gap-3" key={index}>
                        {option.url && (
                          <Link href={`${option.url}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn(
                                'border-dark-blue text-dark-blue hover:bg-periwinkle hover:text-dark-blue',
                                'rounded-full px-4 py-3 text-left text-sm break-words whitespace-normal',
                                'h-auto w-auto max-w-full'
                              )}
                            >
                              {option.label}
                            </Button>
                          </Link>
                        )}

                        {option.next && (
                          <Button
                            variant="outline"
                            size="sm"
                            className={cn(
                              'border-dark-blue text-dark-blue hover:bg-periwinkle hover:text-dark-blue',
                              'rounded-full px-4 py-3 text-left text-sm break-words whitespace-normal',
                              'h-auto w-auto max-w-full'
                            )}
                            onClick={() => {
                              setSteps((prev) => [...prev, option.next]);
                            }}
                          >
                            {option.label}
                          </Button>
                        )}

                        {option.button && option.button}
                      </div>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </main>
      </ScrollArea>
    </main>
  );
}

const Message = ({ content }) => {
  return (
    <div className="message-shadow rounded-2xl border border-gray-100 bg-white px-6 py-3 text-gray-900 sm:px-8">
      {content}
    </div>
  );
};
